import { formatNumber } from "pages/MatchDetail/MatchedOdds";
import React, { useState } from "react";
import { Table } from "react-bootstrap";
import "./styles.scss";
import moment from "moment";

interface FancyPLData {
  match_date: string;
  sport_name: string;
  uplineProfitLoss: number;
  total: number;
  commission: number;
  type: number;
  match_name: string;
  sport_id: string;
  match_id: string;
}

interface FancyPLTableProps {
  fancyPLData: FancyPLData[];
  sport_name: string | undefined;
  onClickRow: (match_id: string, match_name: string) => void;
}

// Sorting function
const sortData = <T extends object>(
  data: T[],
  sortKey: keyof T | "",
  sortOrder: "asc" | "desc"
): T[] => {
  if (!sortKey) return data;

  return [...data].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];

    // Sorting for string values
    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortOrder === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Sorting for numeric values
    const aNum = typeof aVal === "number" ? aVal : parseFloat(aVal as string);
    const bNum = typeof bVal === "number" ? bVal : parseFloat(bVal as string);

    if (isNaN(aNum) || isNaN(bNum)) return 0;

    return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
  });
};

// Get sorting class for the column
const getSortClass = (
  key: string,
  sortKey: string,
  sortOrder: "asc" | "desc"
) => {
  const isActive = sortKey === key;
  const upClass = isActive && sortOrder === "asc" ? "text-dark" : "text-muted";
  const downClass =
    isActive && sortOrder === "desc" ? "text-dark" : "text-muted";

  return (
    <span className="d-flex flex-column ms-2">
      <i
        className={`bi bi-caret-up-fill ${upClass}`}
        style={{ lineHeight: 0.6, cursor: "pointer" }}
      />
      <i
        className={`bi bi-caret-down-fill ${downClass}`}
        style={{ lineHeight: 0.6, cursor: "pointer" }}
      />
    </span>
  );
};

const FancyPLMatchWiseTableEventPl: React.FC<FancyPLTableProps> = ({
  fancyPLData,
  sport_name,
  onClickRow,
}) => {
  // State for sorting
  const [sortKey, setSortKey] = useState<keyof FancyPLData | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Handle column sorting
  const handleSort = (key: keyof FancyPLData) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  // Get sorted data based on sortKey and sortOrder
  const sortedData = sortData(fancyPLData, sortKey, sortOrder);

  return (
    <div className="table-responsive">
      <Table striped hover responsive className="table-centered align-middle">
        <thead className="table-light">
          <tr>
            <th
              onClick={() => handleSort("sport_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Sport Name</span>
                {getSortClass("sport_name", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("match_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Event Date</span>
                {getSortClass("match_name", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("match_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Event Name</span>
                {getSortClass("match_name", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("total")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Profit/Loss</span>
                {getSortClass("total", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("uplineProfitLoss")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Downline Profit/Loss</span>
                {getSortClass("uplineProfitLoss", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("commission")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Commission</span>
                {getSortClass("commission", String(sortKey), sortOrder)}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => {
            const Pl =
              -1 * (Number(item.total ?? 0) + Number(item.commission ?? 0));
            return (
              <tr
                key={index}
                onClick={() => onClickRow(item.match_id, item.match_name)}
                style={{ cursor: "pointer" }}
              >
                <td>{sport_name}</td>
                <td>{moment(item.match_date).format("DD-MM-YYYY HH:mm:ss")}</td>
                <td>{item.match_name}</td>
                <td className={Pl < 0 ? "text-danger" : "text-success"}>
                  {formatNumber(Pl.toFixed(2))}
                </td>
                <td
                  className={
                    Number(item.total ?? 0) < 0 ? "text-danger" : "text-success"
                  }
                >
                  {formatNumber(Number(item.total ?? 0).toFixed(2))}
                </td>
                <td
                  className={
                    -1 * item.commission < 0 ? "text-danger" : "text-success"
                  }
                >
                  {(-1 * item.commission)?.toFixed(2)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default FancyPLMatchWiseTableEventPl;
